import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A ~100-line "sonner-lite": stacked, auto-dismissing toasts rendered into a polite live
 * region so async results (saved, deleted, retry queued) are announced, not just shown.
 */

export type ToastVariant = 'default' | 'success' | 'error';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Milliseconds before auto-dismiss. Errors stay longer. */
  duration?: number;
}

interface ToastRecord extends Required<Pick<ToastOptions, 'title' | 'variant'>> {
  id: number;
  description?: string;
  duration: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const VARIANT_STYLES: Record<ToastVariant, string> = {
  default: 'border-border bg-popover text-popover-foreground',
  success: 'border-success/40 bg-popover text-popover-foreground',
  error: 'border-destructive/50 bg-popover text-popover-foreground',
};

const VARIANT_ICONS: Record<ToastVariant, typeof Info> = {
  default: Info,
  success: CheckCircle2,
  error: AlertTriangle,
};

const VARIANT_ICON_STYLES: Record<ToastVariant, string> = {
  default: 'text-muted-foreground',
  success: 'text-success',
  error: 'text-destructive',
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const variant = options.variant ?? 'default';
      const record: ToastRecord = {
        id: nextId++,
        title: options.title,
        description: options.description,
        variant,
        duration: options.duration ?? (variant === 'error' ? 8000 : 4500),
      };
      setToasts((current) => [...current.slice(-3), record]);
      const timer = setTimeout(() => dismiss(record.id), record.duration);
      timers.current.set(record.id, timer);
    },
    [dismiss],
  );

  // Capture the ref value now — the cleanup runs after the ref may have been detached.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function Toaster({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
    >
      <ol aria-live="polite" aria-atomic="false" className="flex w-full flex-col gap-2 sm:w-96">
        {toasts.map((entry) => {
          const Icon = VARIANT_ICONS[entry.variant];
          return (
            <li
              key={entry.id}
              className={cn(
                'pointer-events-auto flex animate-toast-in items-start gap-3 rounded-lg border p-4 shadow-lg',
                VARIANT_STYLES[entry.variant],
              )}
            >
              <Icon
                aria-hidden="true"
                className={cn('mt-0.5 h-4 w-4 shrink-0', VARIANT_ICON_STYLES[entry.variant])}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{entry.title}</p>
                {entry.description ? (
                  <p className="mt-1 break-words text-sm text-muted-foreground">
                    {entry.description}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onDismiss(entry.id)}
                className={cn(
                  'shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <X aria-hidden="true" className="h-4 w-4" />
                <span className="sr-only">Dismiss notification</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>,
    document.body,
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside <ToastProvider>');
  return context;
}
